import { FoodPortioningStatusType, Kitchen, Localized, Session, User, UserInput } from './types';

export enum FoodPortioningRecordVariant {
  meals = 'meals',
  sauces = 'sauces'
}

export enum FoodPortioningMealType {
  regular = 'regular',
  mcm = 'mcm',
  kids = 'kids',
  addons = 'addons',
  cafe = 'cafe'
}

export enum FoodCategory {
  breakfast = 'breakfast',
  lunchDinner = 'lunchDinner',
  snack = 'snack',
  salad = 'salad',
  addons = 'addons',
  custom = 'custom'
}

export type LocalizedEnOnly = { en: string };

export type FoodPortioningRecordRow = {
  key: string;
  foodId?: string | null;
  foodName?: LocalizedEnOnly | null;
  componentId?: string | null;
  componentName?: LocalizedEnOnly | null;
  quantityTrays?: string | null;
  packingRoomTemp?: string | null;
  portioningStartTime?: string | null;
  portioningStartTemp?: string | null;
  portioningFinishTime?: string | null;
  portioningFinishTemp?: string | null;
  numberOfPortions?: string | null;
  correctiveAction?: string | null;
  isDeletedFromMenu?: boolean | null;
  mealType?: FoodPortioningMealType | null;
  portionTypes?: FoodPortioningStatusType[] | null;
  isBusiness?: boolean | null;
};

export type FoodPortioningRecord = {
  id: string;
  day: string;
  shift: Session;
  kitchen: Kitchen;
  variant: FoodPortioningRecordVariant;
  location?: string | null;
  rows: FoodPortioningRecordRow[];
  isFinalized: boolean;
  finalizedBy?: User | null;
  finalizedAt?: string | null;
  contributors: string[];
  updatedBy?: User | null;
  updatedAt?: string | null;
  createdBy?: User | null;
  createdAt?: string | null;
};

export type FoodPortioningRecordRowPatchInput = {
  key: string;
  quantityTrays?: string;
  packingRoomTemp?: string;
  portioningStartTime?: string;
  portioningStartTemp?: string;
  portioningFinishTime?: string;
  portioningFinishTemp?: string;
  numberOfPortions?: string;
  correctiveAction?: string;
};

export type UpdateFoodPortioningRecordInput = {
  kitchen: Kitchen;
  day: string;
  shift: Session;
  variant: FoodPortioningRecordVariant;
  location?: string;
  rows?: FoodPortioningRecordRowPatchInput[];
  user: UserInput;
};

export type GetFoodPortioningRecordQueryVariables = {
  kitchen: Kitchen;
  day: string;
  shift: Session;
  variant: FoodPortioningRecordVariant;
};

export type GetFoodPortioningRecordQuery = {
  getFoodPortioningRecord?: FoodPortioningRecord | null;
};

export type ExportFoodPortioningRecordQueryVariables = GetFoodPortioningRecordQueryVariables;

export type ExportFoodPortioningRecordQuery = {
  exportFoodPortioningRecord?: string | null;
};

export type UpdateFoodPortioningRecordMutationVariables = {
  input: UpdateFoodPortioningRecordInput;
};

export type UpdateFoodPortioningRecordMutation = {
  updateFoodPortioningRecord?: FoodPortioningRecord | null;
};

export type FinalizeFoodPortioningRecordMutationVariables = {
  kitchen: Kitchen;
  day: string;
  shift: Session;
  variant: FoodPortioningRecordVariant;
  user: UserInput;
};

export type FinalizeFoodPortioningRecordMutation = {
  finalizeFoodPortioningRecord?: FoodPortioningRecord | null;
};

export type SubscribeToFoodPortioningRecordChangesSubscriptionVariables = {
  kitchen: Kitchen;
  day: string;
};

export type SubscribeToFoodPortioningRecordChangesSubscription = {
  subscribeToFoodPortioningRecordChanges?: FoodPortioningRecord | null;
};

export type BaggingDispatchRecordRow = {
  key: string;
  category: FoodCategory;
  foodId?: string | null;
  foodName?: Localized | null;
  foodSize?: string | null;
  orders?: number | null;
  actual?: number | null;
  extraMeal?: number | null;
  leftOver?: number | null;
  pRoomTemp?: number | null;
  mealTempStart?: number | null;
  mealTempEnd?: number | null;
  correctiveAction?: string | null;
  isDeletedFromMenu?: boolean | null;
};

export type BaggingDispatchRecord = {
  id: string;
  day: string;
  shift: Session;
  kitchen: Kitchen;
  location?: string | null;
  baggingStartTime?: string | null;
  baggingEndTime?: string | null;
  rows: BaggingDispatchRecordRow[];
  isFinalized: boolean;
  finalizedBy?: User | null;
  finalizedAt?: string | null;
  contributors: string[];
  updatedBy?: User | null;
  updatedAt?: string | null;
  createdBy?: User | null;
  createdAt?: string | null;
};

export type BaggingDispatchRecordRowPatchInput = {
  key: string;
  actual?: number;
  extraMeal?: number;
  leftOver?: number;
  pRoomTemp?: number;
  mealTempStart?: number;
  mealTempEnd?: number;
  correctiveAction?: string;
};

export type UpdateBaggingDispatchRecordInput = {
  kitchen: Kitchen;
  day: string;
  shift: Session;
  location?: string;
  baggingStartTime?: string;
  baggingEndTime?: string;
  rows?: BaggingDispatchRecordRowPatchInput[];
  user: UserInput;
};

export type GetBaggingDispatchRecordQueryVariables = {
  kitchen: Kitchen;
  day: string;
  shift: Session;
};

export type GetBaggingDispatchRecordQuery = {
  getBaggingDispatchRecord?: BaggingDispatchRecord | null;
};

export type ExportBaggingDispatchRecordQueryVariables = GetBaggingDispatchRecordQueryVariables;

export type ExportBaggingDispatchRecordQuery = {
  exportBaggingDispatchRecord?: string | null;
};

export type UpdateBaggingDispatchRecordMutationVariables = {
  input: UpdateBaggingDispatchRecordInput;
};

export type UpdateBaggingDispatchRecordMutation = {
  updateBaggingDispatchRecord?: BaggingDispatchRecord | null;
};

export type FinalizeBaggingDispatchRecordMutationVariables = {
  kitchen: Kitchen;
  day: string;
  shift: Session;
  user: UserInput;
};

export type FinalizeBaggingDispatchRecordMutation = {
  finalizeBaggingDispatchRecord?: BaggingDispatchRecord | null;
};

export type SubscribeToBaggingDispatchRecordChangesSubscriptionVariables = {
  kitchen: Kitchen;
  day: string;
};

export type SubscribeToBaggingDispatchRecordChangesSubscription = {
  subscribeToBaggingDispatchRecordChanges?: BaggingDispatchRecord | null;
};

export const FoodCategoryLabel: Record<FoodCategory, string> = {
  [FoodCategory.breakfast]: 'Breakfast',
  [FoodCategory.lunchDinner]: 'Lunch & Dinner',
  [FoodCategory.snack]: 'Snack',
  [FoodCategory.salad]: 'Salad',
  [FoodCategory.addons]: 'Add ons',
  [FoodCategory.custom]: 'Custom meals'
};

export const FOOD_CATEGORY_ORDER: FoodCategory[] = [
  FoodCategory.breakfast,
  FoodCategory.lunchDinner,
  FoodCategory.snack,
  FoodCategory.salad,
  FoodCategory.addons,
  FoodCategory.custom
];
